import { z } from 'zod';

import {
  BoardTypeSchema,
  IsoDateTimeSchema,
  IssueTypeSchema,
  ProjectRoleSchema,
  UuidSchema,
} from './common.js';
import { UserRefSchema } from './user.js';

export const ProjectSchema = z.object({
  id: UuidSchema,
  key: z.string(),
  name: z.string(),
  description: z.string(),
  boardType: BoardTypeSchema,
  isArchived: z.boolean(),
  myRole: ProjectRoleSchema.nullable(),
  counts: z.object({
    open: z.number().int(),
    done: z.number().int(),
    members: z.number().int(),
  }),
  createdAt: IsoDateTimeSchema,
});
export type Project = z.infer<typeof ProjectSchema>;

export const CreateProjectBodySchema = z
  .object({
    key: z
      .string()
      .regex(/^[A-Z][A-Z0-9]{1,9}$/, 'Ключ: 2–10 символов, заглавные латинские и цифры, начинается с буквы'),
    name: z.string().min(1, 'Введите название').max(120),
    description: z.string().max(10_000).default(''),
    boardType: BoardTypeSchema.default('kanban'),
  })
  .strict();
export type CreateProjectBody = z.infer<typeof CreateProjectBodySchema>;

export const UpdateProjectBodySchema = z
  .object({
    name: z.string().min(1).max(120).optional(),
    description: z.string().max(10_000).optional(),
    boardType: BoardTypeSchema.optional(),
  })
  .strict();
export type UpdateProjectBody = z.infer<typeof UpdateProjectBodySchema>;

export const ListProjectsQuerySchema = z.object({
  archived: z
    .enum(['true', 'false', 'all'])
    .default('false')
    .transform((value) => value),
});

export const MemberSchema = z.object({
  user: UserRefSchema,
  role: ProjectRoleSchema,
  createdAt: IsoDateTimeSchema,
});
export type Member = z.infer<typeof MemberSchema>;

export const AddMemberBodySchema = z
  .object({
    userId: UuidSchema,
    role: ProjectRoleSchema.default('member'),
  })
  .strict();
export type AddMemberBody = z.infer<typeof AddMemberBodySchema>;

export const UpdateMemberBodySchema = z
  .object({ role: ProjectRoleSchema })
  .strict();
export type UpdateMemberBody = z.infer<typeof UpdateMemberBodySchema>;

export const RemoveMemberQuerySchema = z.object({
  reassign: z.enum(['keep', 'null']).default('keep'),
});

export const StatusSchema = z.object({
  id: UuidSchema,
  /** Workflow, которому принадлежит статус (docs/03 §1). */
  workflowId: UuidSchema,
  name: z.string(),
  category: z.enum(['todo', 'in_progress', 'done']),
  position: z.number().int(),
  wipLimit: z.number().int().nullable(),
  isInitial: z.boolean(),
  /**
   * Ответственный по умолчанию. При переходе в статус исполнитель меняется на
   * него; `null` — прежний ответственный сохраняется.
   */
  defaultAssigneeId: UuidSchema.nullable(),
});
export type Status = z.infer<typeof StatusSchema>;

export const TransitionSchema = z.object({
  id: UuidSchema,
  /** Workflow перехода: статусы перехода живут только в нём (docs/03 §1). */
  workflowId: UuidSchema,
  fromStatusId: UuidSchema,
  toStatusId: UuidSchema,
  name: z.string(),
  onlyAssignee: z.boolean(),
  requireResolution: z.boolean(),
});
export type Transition = z.infer<typeof TransitionSchema>;

export const WorkflowSchema = z.object({
  statuses: z.array(StatusSchema),
  transitions: z.array(TransitionSchema),
  problems: z.array(z.string()),
});
export type Workflow = z.infer<typeof WorkflowSchema>;

/**
 * Workflow проекта: сам набор плюс типы задач, которые он ведёт (docs/05 §5).
 * Проект может держать несколько workflow; один из них — по умолчанию.
 */
export const WorkflowSummarySchema = z.object({
  id: UuidSchema,
  projectId: UuidSchema,
  name: z.string(),
  isDefault: z.boolean(),
  /** Типы задач, привязанные к этому workflow. */
  issueTypes: z.array(IssueTypeSchema),
  /**
   * Сколько задач каждого типа сейчас в статусах этого workflow.
   *
   * Тип привязан ровно к одному workflow, поэтому эти числа — ещё и ответ на
   * вопрос «сколько задач переедет», если тип переключат на другой флоу
   * (docs/03 §1). Интерфейс показывает их в подтверждении.
   */
  issueTypeCounts: z.array(
    z.object({ issueType: IssueTypeSchema, value: z.number().int().min(0) }),
  ),
  statuses: z.array(StatusSchema),
  transitions: z.array(TransitionSchema),
  problems: z.array(z.string()),
});
export type WorkflowSummary = z.infer<typeof WorkflowSummarySchema>;

export const WorkflowListSchema = z.object({
  items: z.array(WorkflowSummarySchema),
});
export type WorkflowList = z.infer<typeof WorkflowListSchema>;

export const CreateWorkflowBodySchema = z
  .object({
    name: z.string().trim().min(1, 'Введите название').max(80),
    /** Скопировать статусы и переходы существующего workflow. */
    copyFromWorkflowId: UuidSchema.optional(),
  })
  .strict();
export type CreateWorkflowBody = z.infer<typeof CreateWorkflowBodySchema>;

export const UpdateWorkflowBodySchema = z
  .object({ name: z.string().trim().min(1, 'Введите название').max(80) })
  .strict();
export type UpdateWorkflowBody = z.infer<typeof UpdateWorkflowBodySchema>;

/** Привязка типа задачи к workflow: тип переезжает в новый флоу (docs/03 §1). */
export const BindIssueTypeBodySchema = z
  .object({ workflowId: UuidSchema })
  .strict();
export type BindIssueTypeBody = z.infer<typeof BindIssueTypeBodySchema>;

export const CreateStatusBodySchema = z
  .object({
    name: z.string().min(1, 'Введите название').max(50),
    category: z.enum(['todo', 'in_progress', 'done']),
    wipLimit: z.number().int().min(0).nullable().optional(),
    position: z.number().int().min(0).optional(),
    defaultAssigneeId: UuidSchema.nullable().optional(),
    /** Без него статус создаётся в workflow по умолчанию (docs/05 §5). */
    workflowId: UuidSchema.optional(),
  })
  .strict();
export type CreateStatusBody = z.infer<typeof CreateStatusBodySchema>;

export const UpdateStatusBodySchema = z
  .object({
    name: z.string().min(1).max(50).optional(),
    category: z.enum(['todo', 'in_progress', 'done']).optional(),
    wipLimit: z.number().int().min(0).nullable().optional(),
    position: z.number().int().min(0).optional(),
    defaultAssigneeId: UuidSchema.nullable().optional(),
  })
  .strict();
export type UpdateStatusBody = z.infer<typeof UpdateStatusBodySchema>;

export const ReorderStatusesBodySchema = z
  .object({
    ids: z.array(UuidSchema).min(1, 'Нужен хотя бы один статус'),
  })
  .strict();
export type ReorderStatusesBody = z.infer<typeof ReorderStatusesBodySchema>;

export const CreateTransitionBodySchema = z
  .object({
    fromStatusId: UuidSchema,
    toStatusId: UuidSchema,
    name: z.string().min(1).max(50).optional(),
    onlyAssignee: z.boolean().default(false),
    requireResolution: z.boolean().default(false),
    /** Workflow перехода; без него — workflow по умолчанию (docs/05 §5). */
    workflowId: UuidSchema.optional(),
  })
  .strict();
export type CreateTransitionBody = z.infer<typeof CreateTransitionBodySchema>;

export const UpdateTransitionBodySchema = z
  .object({
    name: z.string().min(1).max(50).optional(),
    onlyAssignee: z.boolean().optional(),
    requireResolution: z.boolean().optional(),
  })
  .strict();
export type UpdateTransitionBody = z.infer<typeof UpdateTransitionBodySchema>;

/**
 * Провайдер Git/PR (docs/18 §5.5). `other` — репозиторий только для чтения:
 * разработчик к такому проекту не привязывается.
 */
export const GitProviderSchema = z.enum(['github', 'gitlab', 'other']);
export type GitProvider = z.infer<typeof GitProviderSchema>;

/** Команда проверки разработчика: выполняется в песочнице после DSH. */
export const DeveloperCheckSchema = z
  .object({
    name: z.string().trim().min(1).max(40),
    command: z.string().trim().min(1).max(500),
    timeoutSeconds: z.number().int().min(1).max(3600).default(600),
  })
  .strict();
export type DeveloperCheck = z.infer<typeof DeveloperCheckSchema>;

export const DeveloperChecksSchema = z.array(DeveloperCheckSchema).max(5);
export type DeveloperChecks = z.infer<typeof DeveloperChecksSchema>;

/**
 * Репозиторий проекта (docs/17 §5.5, docs/18 §5.5): у каждого проекта свой
 * URL, ветка, read-only токен, write+PR токен и проверки. Секреты наружу не
 * возвращаются — только `hasToken` / `hasWriteToken`.
 */
export const ProjectRepositorySchema = z.object({
  url: z.string().nullable(),
  branch: z.string(),
  provider: GitProviderSchema.optional(),
  providerBaseUrl: z.string().nullable().optional(),
  hasToken: z.boolean(),
  hasWriteToken: z.boolean().optional(),
  checks: DeveloperChecksSchema.optional(),
  updatedAt: IsoDateTimeSchema.nullable(),
  writeUpdatedAt: IsoDateTimeSchema.nullable().optional(),
});
export type ProjectRepository = z.infer<typeof ProjectRepositorySchema>;

export const UpdateProjectRepositoryBodySchema = z
  .object({
    /** HTTPS-URL без учётных данных; публичные хосты, приватные запрещены. */
    url: z.string().trim().url().max(500),
    branch: z.string().trim().min(1).max(100).optional(),
    provider: GitProviderSchema.optional(),
    /** API base URL для self-hosted GitHub/GitLab; null — публичный. */
    providerBaseUrl: z.string().trim().url().max(500).nullable().optional(),
    /** Пусто — оставить прежний read-only токен. */
    token: z.string().min(1).max(2000).optional(),
    /** Пусто — оставить прежний write+PR токен. */
    writeToken: z.string().min(1).max(2000).optional(),
    /** Удалить write+PR токен (нельзя вместе с writeToken). */
    clearWriteToken: z.boolean().optional(),
    checks: DeveloperChecksSchema.optional(),
  })
  .strict()
  .refine((value) => !(value.writeToken !== undefined && value.clearWriteToken === true), {
    path: ['clearWriteToken'],
    message: 'Нельзя одновременно задать и удалить write-токен',
  });
export type UpdateProjectRepositoryBody = z.infer<typeof UpdateProjectRepositoryBodySchema>;

export const ProjectRepositoryTestSchema = z.object({
  ok: z.boolean(),
  commit: z.string().nullable(),
  branch: z.string(),
  testedAt: IsoDateTimeSchema,
});
export type ProjectRepositoryTest = z.infer<typeof ProjectRepositoryTestSchema>;

/** Проверка прав разработчика: push и создание PR (docs/18 §5.5). */
export const ProjectRepositoryWriteTestSchema = z.object({
  ok: z.boolean(),
  canPush: z.boolean(),
  canCreatePr: z.boolean(),
  login: z.string().nullable(),
  provider: GitProviderSchema,
  testedAt: IsoDateTimeSchema,
});
export type ProjectRepositoryWriteTest = z.infer<typeof ProjectRepositoryWriteTestSchema>;
